import useCostumizationApi from "views/Settings/Customization/hooks/useCostumizationApi";
import { Col, Descriptions, Form, Modal, notification, Row } from "antd";
import MultiEmailInput from "components/MultiEmailInput/MultiEmailInput";
import { formatNumberWithSpaces } from "utils/formatNumberWithSpaces";
import useNotification from "utils/hooks/useNotification";
import { customToFixed } from "utils/toFixedNumber";
import { getFormError } from "utils/getFormError";
import useSendEmail from "../hooks/useSendEmail";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { FaSave } from "react-icons/fa";
import { MdMail } from "react-icons/md";
import { TextEditor } from "@neonumy/ant-design";
import TooltipButton from "views/Documents/components/TooltipButton";
interface SendEmailPropsTypes {
  id: ID;
  email: string;
  reference: string;
  amount: number;
  clientName: string;
  currency: string;
  decimalPlaces: number;
}

export default function SendEmail(props: SendEmailPropsTypes): React.ReactElement {
  const { id, email, reference, amount, clientName, currency, decimalPlaces } = props;
  const [form] = Form.useForm();
  const urlParams: RouteParams = useParams();

  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { sendPaymentEmail } = useSendEmail();
  const { notificationError } = useNotification();
  const { getCompanyEmailTemplate, emailTemplateData } = useCostumizationApi();
  const receivers = email ? [email] : [];

  useEffect(() => {
    if (id && isModalVisible) {
      getCompanyEmailTemplate({
        variables: {
          companyId: urlParams?.companyId,
          paymentId: id,
        },
      });
    }
  }, [isModalVisible]);

  useEffect(() => {
    if (emailTemplateData && receivers) {
      form.setFields([
        {
          name: "receivers",
          value: receivers,
        },
        {
          name: "title",
          value: emailTemplateData?.titleThankYouForPayment,
        },
        {
          name: "content",
          value: emailTemplateData?.contentThankYouForPayment,
        },
      ]);
    }
  }, [emailTemplateData, receivers]);

  const onFinish = async (values: Record<string, any>) => {
    let errors: any = [];

    const variables = {
      input: {
        paymentId: id,
        receivers: values?.receivers,
        title: values?.title,
        content: values?.content,
      },
    };

    const response = await sendPaymentEmail({ variables });
    errors = response?.data?.sendPaymentEmail?.errors;
    if (errors && errors.length > 0) {
      const fieldsData = getFormError(errors, values);
      notificationError(errors);
      form.setFields(fieldsData);
    } else {
      notification.success({
        message: t("Email"),
        description: t("Successfully sent"),
        placement: "bottomLeft",
      });
      setIsModalVisible(false);
      form.resetFields();
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleOk = () => {
    form.submit();
  };

  return (
    <>
      {id && <TooltipButton placement="top" handleOnClick={showModal} icon={<MdMail />} />}
      <Modal
        title={t("Send email")}
        open={isModalVisible}
        onOk={handleOk}
        okText={t("Save")}
        okButtonProps={{
          icon: (
            <span className="anticon">
              <FaSave size={18} />
            </span>
          ),
        }}
        onCancel={handleCancel}
        width={960}
        className="antd-modal"
      >
        <Form
          name="sendByEmailModal"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
        >
          <Row gutter={[8, 8]}>
            <Col xs={24} sm={24}>
              <Form.Item
                name="receivers"
                label={t("Receivers")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <MultiEmailInput />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24}>
              <Form.Item
                name="title"
                label={t("Object")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <TextEditor allowImageUpload={false} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24}>
              <Form.Item
                name="content"
                label={t("Message")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <TextEditor allowImageUpload={false} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Descriptions title={t("Information")} size="small" column={1} bordered>
                <Descriptions.Item label={t("Client")}>{clientName}</Descriptions.Item>
                <Descriptions.Item label={t("Reference")}>{reference}</Descriptions.Item>
                <Descriptions.Item label={t("Amount")}>
                  {formatNumberWithSpaces(customToFixed(Number(amount), decimalPlaces))} {currency}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
